import onDocumentLoaded from './onDocumentLoaded';

export interface SiteAnimationsConfig {
	observeSelector: string;
	ancestorSelector?: string;
	minimum?: number;
	minimumUnit?: "px" | "%";
}

/* TODO: Improvements:
	* Settings for both x and y axis that are separate
*/

export default class InViewObserver {
	private config: SiteAnimationsConfig;

	static createInstance(configs: SiteAnimationsConfig[]): InViewObserver[] {
		if ("IntersectionObserver" in window === false) {
			return; // Don't run because browser does not support
		}

		const inViews = new Array<InViewObserver>();
		configs.forEach((config) => {
			inViews.push(new InViewObserver(config));
		});
		return inViews;
	}

	private constructor(config: SiteAnimationsConfig) {
		this.config = config;
		if (this.config.minimum === undefined) {
			this.config.minimum = 20;
			this.config.minimumUnit = "px";
		}
		if (this.config.minimumUnit === undefined) {
			this.config.minimumUnit = "px";
		}
		onDocumentLoaded(document, () => {
			this.createObserver(config);
		});
	}

	private createObserver(config: SiteAnimationsConfig): void {
		const observerElements: NodeList = document.querySelectorAll(config.observeSelector);
		if (observerElements.length === 0) {
			return;
		}

		const thresholds = createThresholds(0, 1, 0.005);

		const observer: IntersectionObserver = new IntersectionObserver(
			this.onIntersectionObserve,
			{ threshold: thresholds }
		);

		observerElements.forEach(node => {
			if (node instanceof Element) {
				const element = node as Element;

				const container = this.getAncestorOrSelf(element);
				container.classList.add("inViewObserver");

				// Give a frame for the setup to take affect and then start observing (since the element may already be in view)
				window.requestAnimationFrame(() => {
					observer.observe(element);
				});
			}
		});
	}

	private onIntersectionObserve = (entries: Array<IntersectionObserverEntry>, observer: IntersectionObserver) => {
		entries.forEach((entry) => {
			let isInView = false;
			
			if (this.config.minimumUnit === "px") {
				const minWidth = Math.min(this.config.minimum, entry.rootBounds.width, entry.boundingClientRect.width);
				const minHeight = Math.min(this.config.minimum, entry.rootBounds.height, entry.boundingClientRect.height);
				if (
					entry.intersectionRect.width		>= minWidth
					&& entry.intersectionRect.height	>= minHeight
				) {
					isInView = true;
				}
			} else {
				const minWidth = Math.min(entry.rootBounds.width, entry.boundingClientRect.width);
				const minHeight = Math.min(entry.rootBounds.height, entry.boundingClientRect.height);
				if (
					entry.intersectionRect.width / minWidth >= this.config.minimum / 100
					&& entry.intersectionRect.height / minHeight >= this.config.minimum / 100
				) {
					isInView = true;
				}
			}
			
			if (isInView) {
				const container = this.getAncestorOrSelf(entry.target);
				container.classList.add("inViewObserver--once");
				container.classList.add("inViewObserver--now");
			} else {
				const container = this.getAncestorOrSelf(entry.target);
				container.classList.remove("inViewObserver--now");
			}
		});
	}

	private getAncestorOrSelf(element: Element): Element{
		if (this.config.ancestorSelector === undefined) {
			return element;
		}
		const ancestor = element.closest(this.config.ancestorSelector);
		return ancestor !== null ? ancestor : element;
	}
}

function createThresholds(start: number, end: number, step: number) {
	// ex.  0, .005, .01, .015, .02
	let array = [];
	for (let i = start; i <= end; i += step) {
		array.push(Math.round(i * 1000) / 1000);
	}
	return array;
}